/**
 * The `statusReason` an operator's force-converge stamps, as ONE vocabulary the
 * stamping use cases format through and the roster classifier reads through.
 *
 * WHY ONE DEFINITION. The roster decides whether a `DEGRADED` cluster pages, and
 * it read only the drift counts line. A forced converge writes its own shape —
 * the prefix, the operator's words and, over a cluster that was already
 * DEGRADED, the whole previous reason wrapped in `(was: …)` — so every forced
 * tenant read as a fault and `cluster-degraded` paged five production tenants on
 * 2026-10-09 while they were being converged on purpose. The shape is now spelled
 * here once, and the classifier judges a forced reason by what it wraps.
 *
 * Three shapes, all opening with the prefix:
 *
 *   - forced from HEALTHY — `<prefix><operator reason>`;
 *   - a server roll       — `<prefix>server roll <iso>: <operator reason>`;
 *   - forced over DEGRADED — `<prefix><operator reason> (was: <previous reason>)`,
 *     the previous reason WHOLE, multi-line, its `)` closing the last line.
 *
 * The reconcile loop reads the prefix as the operator having chosen this moment
 * (`reconcile-brake.ts`), so the prefix is the authority and these three are the
 * only spellings of it.
 */
export const TENANT_CLUSTER_REASON_FORCED_CONVERGE_PREFIX =
  "forced converge requested by an operator: ";

/** What a re-stamp wraps when the DEGRADED cluster carried no reason at all. */
export const FORCED_CONVERGE_NO_PRIOR_REASON = "no reason recorded";

const WAS_OPEN = " (was: ";
const WAS_CLOSE = ")";
/** What an operator's own ` (was: ` becomes, so the wrap's delimiter stays unique. */
const WAS_OPEN_ESCAPED = " (was ";

/**
 * The operator's text with the wrap's delimiter defused. The reason is free text
 * from the admin route; left as typed, a reason holding ` (was: ` would be read
 * as the start of a wrapped previous reason.
 */
function operatorText(operatorReason: string): string {
  return operatorReason.split(WAS_OPEN).join(WAS_OPEN_ESCAPED);
}

/** A forced converge of a cluster that was not DEGRADED: nothing previous to keep. */
export function formatForcedConvergeReason(operatorReason: string): string {
  return `${TENANT_CLUSTER_REASON_FORCED_CONVERGE_PREFIX}${operatorText(operatorReason)}`;
}

/** An operator's server roll, stamped as a forced converge of the generation it names. */
export function formatServerRollForcedConvergeReason(
  requestedAt: Date,
  operatorReason: string
): string {
  return formatForcedConvergeReason(`server roll ${requestedAt.toISOString()}: ${operatorReason}`);
}

/**
 * A forced converge of a cluster that was already DEGRADED. The previous reason
 * is kept WHOLE, because it is the field the next person debugging the cluster
 * reads — and because it is what says whether the forced work is routine.
 */
export function formatForcedConvergeOverReason(
  operatorReason: string,
  previousReason: string | null
): string {
  return (
    `${formatForcedConvergeReason(operatorReason)}${WAS_OPEN}` +
    `${previousReason ?? FORCED_CONVERGE_NO_PRIOR_REASON}${WAS_CLOSE}`
  );
}

/**
 * A forced reason, read back.
 *
 * - `fresh`  — forced from HEALTHY, or a server roll: operator-requested work
 *              with nothing previous standing behind it.
 * - `over`   — forced over a DEGRADED cluster; `previous` is the reason it
 *              replaced, `null` when that cluster carried none.
 * - `unread` — prefixed, and the wrap does not close: nothing says where the
 *              previous reason ends, so it is not read at all.
 */
export type ForcedConvergeReason =
  | { readonly kind: "fresh" }
  | { readonly kind: "over"; readonly previous: string | null }
  | { readonly kind: "unread" };

/** The forced reason this is, or `null` when it is not a forced converge's. */
export function readForcedConvergeReason(statusReason: string): ForcedConvergeReason | null {
  if (!statusReason.startsWith(TENANT_CLUSTER_REASON_FORCED_CONVERGE_PREFIX)) return null;
  const rest = statusReason.slice(TENANT_CLUSTER_REASON_FORCED_CONVERGE_PREFIX.length);
  // The FIRST opening is the outer wrap's: the operator's text before it was
  // defused, and a nested forced reason sits inside the previous one.
  const open = rest.indexOf(WAS_OPEN);
  if (open < 0) return { kind: "fresh" };
  if (!rest.endsWith(WAS_CLOSE)) return { kind: "unread" };
  const previous = rest.slice(open + WAS_OPEN.length, rest.length - WAS_CLOSE.length);
  return { kind: "over", previous: previous === FORCED_CONVERGE_NO_PRIOR_REASON ? null : previous };
}
